import React from 'react'
import styled from 'styled-components'
import { connect } from 'react-redux'
import { isQcsBot } from 'constants/qcs'
import { withRouter, RouteComponentProps } from 'react-router-dom'
import * as S from '../CarecoAdminDashboard/CarecoAdminStyle'
import { UserIcon, RightIcon, ManagerIcon, CarerIcon, AdminIcon, ServiceUserIcon } from 'uiKit/icons/Icons'
import LabelWithTooltip from 'uiKit/LabelWithTooltip/LabelWithTooltip'
import { isProfessionalsNavAllowed, isServiceUsersNavAllowed } from 'tabs/home/api/usageLimits'

type OverviewData = {
  activeUsers?: number
  archivedUsers?: number
  activeManagers?: number
  archivedManagers?: number
  activeCarers?: number
  archivedCarers?: number
  activeAdmins?: number
  archivedAdmins?: number
  totalUsers?: number
  totalManagers?: number
  totalCarers?: number
  totalAdmins?: number
  activeServiceUsers?: number
  archivedServiceUsers?: number
  totalServiceUsers?: number
} | null

interface OverviewProps extends RouteComponentProps {
  data?: OverviewData
  showUserStatuses?: boolean
  activeBot?: any
}

const StatusBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  width: fit-content;
  padding: 5px 12px;
  border-radius: 6px;
  background: #edf3f2;
  color: var(--color-text-secondary);
  font-size: 14px;
  line-height: 20px;
  white-space: nowrap;

  & > span + span {
    &::before {
      content: '·';
      margin-right: 10px;
    }
  }

  strong { color: var(--color-text-black); }
`

const StatusHeader = styled(S.HeaderLeft)`
  flex-wrap: wrap;
`

const UserStatuses: React.FC<{ active?: number; archived?: number }> = ({ active, archived }) => (
  <StatusBadge>
    <span>Active <strong>{active ?? 0}</strong></span>
    <span>Archived <strong>{archived ?? 0}</strong></span>
  </StatusBadge>
)

const Overview: React.FC<OverviewProps> = ({ history, match, data, activeBot, showUserStatuses = false }) => {
  const botId = (match.params as any)?.botId || activeBot?.id
  const showArchiveCounts = showUserStatuses && !isQcsBot(activeBot || botId)
  const userTotal = (total?: number, active?: number, archived?: number) => {
    if (total != null) return total
    if (showUserStatuses && archived != null) return (active ?? 0) + archived
    return 0
  }
  const totals = {
    users: userTotal(data?.totalUsers, data?.activeUsers, data?.archivedUsers),
    managers: userTotal(data?.totalManagers, data?.activeManagers, data?.archivedManagers),
    carers: userTotal(data?.totalCarers, data?.activeCarers, data?.archivedCarers),
    admins: userTotal(data?.totalAdmins, data?.activeAdmins, data?.archivedAdmins),
    serviceUsers: data?.totalServiceUsers ?? 0,
  }
  const showManagersAndCarers = isProfessionalsNavAllowed(activeBot)
  // Supervision off (and never used) → only admins are listed, so the header counts admins.
  const totalUsersCount = showManagersAndCarers ? totals.users : totals.admins
  const showServiceUsers = data?.totalServiceUsers !== null && isServiceUsersNavAllowed(activeBot)

  const handleNavigation = () => {
    const baseUrl = (match as any)?.url || ''
    history.push(`${baseUrl}/total-users`)
  }

  return (
    <S.Container>
      <S.SectionTitle>Overview</S.SectionTitle>
      <S.OverviewCards>
      <S.Card>
        <S.HeaderRow>
          <StatusHeader>
            <S.IconCircle>
              <UserIcon />
            </S.IconCircle>
            <S.StatContent>
            <S.CardSubtitle>
              Total users: {totalUsersCount}
            </S.CardSubtitle>
            {showArchiveCounts && (showManagersAndCarers ? data?.archivedUsers : data?.archivedAdmins) != null && (
              <UserStatuses
                active={showManagersAndCarers ? data?.activeUsers : data?.activeAdmins}
                archived={showManagersAndCarers ? data?.archivedUsers : data?.archivedAdmins}
              />
            )}
            </S.StatContent>
            <LabelWithTooltip
              text=""
              tooltipId="overview-total-users"

              tooltipText="Total number of people registered across your organisation (all roles combined)"
            />
          </StatusHeader>
          <RightIcon onClick={handleNavigation} />
        </S.HeaderRow>

        <S.StatsGrid>
          {showManagersAndCarers && (
            <>
              <S.StatItem>
                <S.IconCircle>
                  <ManagerIcon />
                </S.IconCircle>
                <S.StatContent>
                  <S.StatLabel>
                    <LabelWithTooltip
                      text="Total managers"
                      tooltipId="overview-total-managers"
                      marginLeft={0}
                      tooltipText="Number of users responsible for supervising carers and overseeing care delivery"
                    />
                  </S.StatLabel>
                  <S.CardSubtitle>{totals.managers}</S.CardSubtitle>
                  {showArchiveCounts && data?.archivedManagers != null && (
                    <UserStatuses active={data?.activeManagers} archived={data?.archivedManagers} />
                  )}
                </S.StatContent>
              </S.StatItem>

              <S.StatItem>
                <S.IconCircle>
                  <CarerIcon />
                </S.IconCircle>
                <S.StatContent>
                  <S.StatLabel>
                    <LabelWithTooltip
                      text="Total carers"
                      tooltipId="overview-total-carers"
                      marginLeft={0}
                      tooltipText="Number of people who use the app but don’t manage or supervise others"
                    />
                  </S.StatLabel>
                  <S.CardSubtitle>{totals.carers}</S.CardSubtitle>
                  {showArchiveCounts && data?.archivedCarers != null && (
                    <UserStatuses active={data?.activeCarers} archived={data?.archivedCarers} />
                  )}
                </S.StatContent>
              </S.StatItem>
            </>
          )}

          {data?.totalAdmins !== null && (
          <S.StatItem>
            <S.IconCircle>
              <AdminIcon />
            </S.IconCircle>
            <S.StatContent>
              <S.StatLabel>
                <LabelWithTooltip
                  text="Total admins"
                  tooltipId="overview-total-admins"
                  marginLeft={0}
                  tooltipText="Number of users managing system access, settings, and organisation configuration"
                />
              </S.StatLabel>
              <S.CardSubtitle>{totals.admins}</S.CardSubtitle>
                  {showArchiveCounts && data?.archivedAdmins != null && (
                    <UserStatuses active={data?.activeAdmins} archived={data?.archivedAdmins} />
                  )}
            </S.StatContent>
          </S.StatItem>
          )}

        </S.StatsGrid>
      </S.Card>

      {showServiceUsers && (
      <S.Card>
        <S.HeaderRow>
          <S.HeaderLeft>
            <S.CardSubtitle>Service users</S.CardSubtitle>
            <LabelWithTooltip
              text=""
              tooltipId="overview-service-users"
              tooltipText="Receives support and care from the organisation"
            />
          </S.HeaderLeft>
        </S.HeaderRow>

        <S.StatsGrid>
          <S.StatItem>
            <S.IconCircle>
              <ServiceUserIcon />
            </S.IconCircle>
            <S.StatContent>
              <S.StatLabel>Total service users</S.StatLabel>
              <S.CardSubtitle>{totals.serviceUsers}</S.CardSubtitle>
              {showArchiveCounts && data?.archivedServiceUsers != null && (
                <UserStatuses active={data?.activeServiceUsers} archived={data?.archivedServiceUsers} />
              )}
            </S.StatContent>
          </S.StatItem>
        </S.StatsGrid>
      </S.Card>
      )}
      </S.OverviewCards>
    </S.Container>
  )
}

const mapStateToProps = (state: { activeBot?: any }) => ({
  activeBot: state.activeBot,
})

export default connect(mapStateToProps)(withRouter(Overview))
